import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import Loader from "./Loader";
import bg from "../assets/bg.jpg";
import logo from "../assets/logo.png";


const Login = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1); // 1: enter email, 2: enter OTP
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  const apiUrl = process.env.REACT_APP_API_URL || "";

  const handleRequestOTP = async () => {
    if (!email) return setMessage("Email is required");
    setLoading(true);
    try {
      const res = await axios.post(`${apiUrl}/auth/request-otp`, { email });
      setMessage(res.data.message);
      setStep(2); // move to OTP input
    } catch (err) {
    const errMsg = err.response?.data?.error || "Error requesting OTP. Please try again.";
    setMessage(errMsg);
    if (!err.response?.data?.error) toast.error(errMsg);
  } finally {
    setLoading(false);
  }
  };

  const handleVerifyOTP = async () => {
    if (!otp) return setMessage("OTP is required");
    try {
      const res = await axios.post(`${apiUrl}/auth/verify-otp`, { email, otp });
      const { token, role } = res.data;
      toast.success("Login successful!");

      // Store JWT and role
      localStorage.setItem("jwt", token);
      localStorage.setItem("role", role);
      localStorage.setItem("email", email);
      // Clear any stale cached name so Navbar always fetches fresh on next load
      localStorage.removeItem("name");

      // Decode name from JWT payload and cache immediately (avoids async delay)
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const name = [payload.firstName, payload.lastName].filter(Boolean).join(' ').trim();
        if (name) localStorage.setItem("name", name);
      } catch { /* non-fatal — Navbar will fetch /users/me */ }

      // Redirect based on role
      if (role === "employee") navigate("/dashboard");
      else if (role === "admin") navigate("/admin-dashboard");
      else if (role === "superuser") navigate("/superuser-dashboard");
      else if (role === "Manager") navigate("/Manager-dashboard");
    } catch (err) {
      setMessage(err.response?.data?.error || "Error verifying OTP");
    }
  };

  // submit request OTP when Enter pressed on email input
  const handleEmailKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleRequestOTP();
    }
  };

  // submit verify OTP when Enter pressed in any OTP input
  const handleOtpKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      const newOtp = otp.split("");
      newOtp[idx] = "";
      setOtp(newOtp.join(""));
      if (idx > 0) {
        const prev = document.getElementById(`otp-input-${idx - 1}`);
        if (prev) prev.focus();
      }
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      const cleaned = (otp || "").replace(/[^0-9]/g, "");
      if (cleaned.length >= 6) {
        handleVerifyOTP();
      } else {
        // focus next empty input if available
        const next = document.getElementById(`otp-input-${Math.min(idx + 1, 5)}`);
        if (next) next.focus();
      }
    }
  };

  return (
    <div
      className="relative flex items-center justify-center"   // changed items-start -> items-center
      style={{
        minHeight: "100vh",           // ensure full viewport height
        backgroundImage: `url(${bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed"
      }}
    >
      {/* subtle overlay so card reads well on image - 20% white + small blur */}
      <div className="absolute inset-0 bg-white/20 backdrop-blur-sm pointer-events-none z-0" />

      <div className="relative w-full max-w-md bg-white rounded-2xl p-8 shadow-lg z-10">
        {/* logo */}
        <div className="flex items-center justify-center mb-0.5">
          <img src={logo} alt="Tasket logo" className="login-logo" />
        </div>

        <h1 className="text-3xl font-bold mb-2 text-gray-900 text-center">Tasket</h1>
        <p className="text-sm text-gray-600 mb-4 text-center">Enter your email to receive a one-time login code.</p>

        {message && <p className="text-sm text-red-600 mb-3">{message}</p>}

        {step === 1 && (
          <>
            <input
              className="w-full p-3 rounded-lg mb-3 bg-white border border-gray-200 text-gray-900 placeholder-gray-400"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleEmailKeyDown}         // <-- added
            />
            <div className="flex gap-3">
              <button
                className="flex-1 py-3 rounded-lg bg-[#7AA33E] text-white font-semibold hover:bg-neon-lime transition"
                onClick={handleRequestOTP}
              >
                {loading ? "Sending..." : "Send OTP"}
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="flex justify-center mb-4">
              {[...Array(6)].map((_, idx) => (
                <input
                  key={idx}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className="w-10 h-12 mx-1 rounded-md border border-gray-200 bg-white text-lg text-center text-gray-900"
                  value={otp[idx] || ""}
                  onChange={e => {
                    const val = e.target.value.replace(/[^0-9]/g, "");
                    if (!val) return;
                    const newOtp = otp.split("");
                    newOtp[idx] = val;
                    setOtp(newOtp.join(""));
                    const next = document.getElementById(`otp-input-${idx + 1}`);
                    if (next) next.focus();
                  }}
                  onKeyDown={e => handleOtpKeyDown(e, idx)}   // <-- replaced inline handler
                  id={`otp-input-${idx}`}
                  autoFocus={idx === 0}
                />
              ))}
            </div>

            <div className="flex gap-3 mb-4">
              <button
                className="flex-1 py-3 rounded-lg bg-[#7AA33E] text-white font-semibold hover:bg-neon-lime transition"
                onClick={handleVerifyOTP}
              >
                Verify OTP
              </button>
              <button
                className="py-3 px-4 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                onClick={() => setStep(1)}
              >
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
